import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { create } from 'zustand'
import { cx } from './Card'
import styles from './Tooltip.module.css'

/**
 * A tooltip for small things on the table (tokens): hover with a mouse, tap on touch. One layer for the whole
 * app, mounted in root.tsx, so it is never clipped by a scroll box or covered by the next seat.
 */
interface TipState {
  id: string | null
  el: HTMLElement | null
  content: ReactNode
  /** Opened by a tap: stays until the next tap. */
  pinned: boolean
  show: (id: string, el: HTMLElement, content: ReactNode, pinned: boolean) => void
  hide: (id?: string) => void
}

const useTip = create<TipState>((set, get) => ({
  id: null,
  el: null,
  content: null,
  pinned: false,
  show: (id, el, content, pinned) => set({ id, el, content, pinned }),
  hide: (id) => {
    if (id === undefined || get().id === id) set({ id: null, el: null, content: null, pinned: false })
  },
}))

const HOVER_DELAY = 120

/**
 * Handlers for the element the tooltip describes. With a mouse it shows on hover and clicks pass through (to
 * the seat underneath); on touch a tap toggles it and goes no further, so reading a token never picks a seat.
 */
export function useTooltip<E extends HTMLElement>(content: () => ReactNode) {
  const id = useId()
  const ref = useRef<E>(null)
  const timer = useRef<number | undefined>(undefined)
  const touch = useRef(false)
  // unmount: drop a pending hover and close this tooltip if it is open
  useEffect(
    () => () => {
      window.clearTimeout(timer.current)
      useTip.getState().hide(id)
    },
    [id],
  )
  const handlers = {
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => {
        if (ref.current) useTip.getState().show(id, ref.current, content(), false)
      }, HOVER_DELAY)
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      window.clearTimeout(timer.current)
      if (!useTip.getState().pinned) useTip.getState().hide(id)
    },
    onPointerDown: (e: React.PointerEvent) => {
      touch.current = e.pointerType !== 'mouse'
    },
    onClick: (e: React.MouseEvent) => {
      if (!touch.current) return
      e.preventDefault()
      e.stopPropagation()
      const tip = useTip.getState()
      if (tip.id === id) tip.hide(id)
      else if (ref.current) tip.show(id, ref.current, content(), true)
    },
  }
  return { ref, handlers }
}

const GAP = 10
const EDGE = 8
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/** Where the box sits relative to its anchor; `arrow` is the point's offset along the box's facing edge. */
type Place = { left: number; top: number; side: 'right' | 'left' | 'above' | 'below'; arrow: number }

/**
 * Beside the anchor (right, else left), so tokens stacked down a card stay in sight; above or below it when
 * neither side has room (narrow phones).
 */
const placeTip = (a: DOMRect, w: number, h: number): Place => {
  const vw = document.documentElement.clientWidth
  const vh = window.innerHeight
  const midX = a.left + a.width / 2
  const midY = a.top + a.height / 2
  const right = a.right + GAP + w <= vw - EDGE
  if (right || a.left - GAP - w >= EDGE) {
    const top = clamp(midY - h / 2, EDGE, vh - h - EDGE)
    return {
      side: right ? 'right' : 'left',
      left: right ? a.right + GAP : a.left - GAP - w,
      top,
      arrow: clamp(midY - top, 14, h - 14),
    }
  }
  const left = clamp(midX - w / 2, EDGE, vw - w - EDGE)
  const above = a.top - GAP - h >= EDGE
  return {
    side: above ? 'above' : 'below',
    left,
    top: above ? a.top - GAP - h : a.bottom + GAP,
    arrow: clamp(midX - left, 14, w - 14),
  }
}

export function TooltipLayer() {
  const { id, el, content, pinned, hide } = useTip()
  const box = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState<Place | null>(null)

  // follow the anchor every frame while open: seats rise on hover, cards slide when they swap
  useLayoutEffect(() => {
    if (!id || !el) {
      setPlace(null)
      return
    }
    let frame = 0
    let last = ''
    const follow = () => {
      if (!el.isConnected) {
        hide(id)
        return
      }
      const tip = box.current
      if (tip) {
        const next = placeTip(el.getBoundingClientRect(), tip.offsetWidth, tip.offsetHeight)
        const key = `${next.side}|${next.left}|${next.top}|${next.arrow}`
        if (key !== last) {
          last = key
          setPlace(next)
        }
      }
      frame = requestAnimationFrame(follow)
    }
    follow()
    return () => cancelAnimationFrame(frame)
  }, [id, el, hide])

  useEffect(() => {
    if (!id) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide()
    }
    const onScroll = () => hide()
    // a tap anywhere but on the token closes a pinned tooltip (the token's own tap toggles it)
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node && el?.contains(e.target))) hide()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('pointerdown', onDown, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('pointerdown', onDown, true)
    }
  }, [id, el, hide])

  if (!id) return null
  return (
    <div
      key={id}
      ref={box}
      role="tooltip"
      className={cx(styles.tip, place && styles[place.side], pinned && styles.pinned)}
      style={
        place
          ? { left: place.left, top: place.top, ['--arrow' as string]: `${place.arrow}px` }
          : { left: 0, top: 0, visibility: 'hidden' }
      }
    >
      {content}
    </div>
  )
}
